/**
 * scDicom.js — файл DICOM Secondary Capture из RGB-картинки. [12.0.57]
 *
 * Чистая сборка байтов, без движка: тест (tests/specrezhimy_test.mjs)
 * собирает файл и разбирает его обратно своим читателем.
 *
 * ПОВОД. Кнопка «DICOM SC» вкладки «Спец. режимы» отдавала файл, который
 * строгий приёмник (станция, PACS) вправе не принять:
 *   • элементы набора шли НЕ по возрастанию тегов — (0010,…) раньше
 *     (0008,…), а PS3.5 §7.1 требует строгого возрастания;
 *   • имя пациента писалось charCodeAt по байту: кириллица превращалась в
 *     младшие байты кодов, без (0008,0005) набора символов;
 *   • при нечётном числе пикселей длина Pixel Data была нечётной (PS3.5 §7.1.1
 *     требует чётную);
 *   • пациент «Anonymous», ID «000000» и СЛУЧАЙНЫЙ UID исследования: снимок не
 *     попадал в исследование, с которого снят;
 *   • модальность «SC» — такого термина в списке модальностей нет; у вторичного
 *     захвата это «OT»; не было обязательного ConversionType (0008,0064).
 * Теперь: теги по возрастанию, строки в UTF-8 с (0008,0005) «ISO_IR 192»,
 * чётные длины, пациент и исследование — с исходного снимка.
 *
 * EXPORTS: собратьSC, ПРИСТАВКА_UID
 */

export const ПРИСТАВКА_UID = '1.2.826.0.1.3680043.8.1055';
const SOP_SC = '1.2.840.10008.5.1.4.1.1.7';      // Secondary Capture Image Storage
const TS_EXPLICIT_LE = '1.2.840.10008.1.2.1';
const ДЛИННЫЕ = new Set(['OB', 'OW', 'OF', 'SQ', 'UT', 'UN']);
const enc = new TextEncoder();

function элемент(g, e, vr, байты) {
  const длинный = ДЛИННЫЕ.has(vr);
  const head = new Uint8Array(длинный ? 12 : 8);
  const dv = new DataView(head.buffer);
  dv.setUint16(0, g, true); dv.setUint16(2, e, true);
  head[4] = vr.charCodeAt(0); head[5] = vr.charCodeAt(1);
  if (длинный) dv.setUint32(8, байты.length, true); else dv.setUint16(6, байты.length, true);
  const out = new Uint8Array(head.length + байты.length);
  out.set(head); out.set(байты, head.length);
  return { g, e, out };
}

function строка(g, e, vr, значение) {
  let б = enc.encode(String(значение ?? ''));
  if (б.length % 2) { const п = new Uint8Array(б.length + 1); п.set(б); п[б.length] = vr === 'UI' ? 0 : 0x20; б = п; }
  return элемент(g, e, vr, б);
}

function us(g, e, v) { const b = new Uint8Array(2); new DataView(b.buffer).setUint16(0, v, true); return элемент(g, e, 'US', b); }
function ul(g, e, v) { const b = new Uint8Array(4); new DataView(b.buffer).setUint32(0, v, true); return элемент(g, e, 'UL', b); }

const неASCII = (v) => /[^\x00-\x7F]/.test(String(v ?? ''));

/**
 * @param {{ширина:number, высота:number, rgb:Uint8Array,
 *   пациент?:{имя?:string, id?:string, рождение?:string, пол?:string},
 *   исследование?:{uid?:string, дата?:string, время?:string, id?:string, accession?:string, описание?:string, направил?:string},
 *   серия?:{uid?:string, номер?:number, описание?:string},
 *   экземпляр?:{uid?:string, номер?:number},
 *   сейчас?:Date, uid?:()=>string}} п
 * @returns {Uint8Array}
 */
export function собратьSC(п) {
  const { ширина: w, высота: h } = п;
  if (!(w > 0) || !(h > 0) || !п.rgb || п.rgb.length < w * h * 3) throw new Error('нет картинки для DICOM SC');
  const сейчас = п.сейчас || new Date();
  const новыйUID = п.uid || (() => `${ПРИСТАВКА_UID}.${сейчас.getTime()}.${Math.floor(Math.random() * 1e9)}`);
  const дата = `${сейчас.getFullYear()}${String(сейчас.getMonth() + 1).padStart(2, '0')}${String(сейчас.getDate()).padStart(2, '0')}`;
  const время = `${String(сейчас.getHours()).padStart(2, '0')}${String(сейчас.getMinutes()).padStart(2, '0')}${String(сейчас.getSeconds()).padStart(2, '0')}`;
  const пац = п.пациент || {}, иссл = п.исследование || {}, сер = п.серия || {}, экз = п.экземпляр || {};
  const sop = экз.uid || новыйUID();
  const строки = [пац.имя, пац.id, иссл.описание, сер.описание, иссл.направил];
  const набор = [
    строки.some(неASCII) ? строка(0x0008, 0x0005, 'CS', 'ISO_IR 192') : null,   // Specific Character Set
    строка(0x0008, 0x0012, 'DA', дата),                       // Instance Creation Date
    строка(0x0008, 0x0013, 'TM', время),                      // Instance Creation Time
    строка(0x0008, 0x0016, 'UI', SOP_SC),                     // SOP Class UID
    строка(0x0008, 0x0018, 'UI', sop),                        // SOP Instance UID
    строка(0x0008, 0x0020, 'DA', иссл.дата || дата),          // Study Date
    строка(0x0008, 0x0030, 'TM', иссл.время || время),        // Study Time
    строка(0x0008, 0x0050, 'SH', иссл.accession || ''),       // Accession Number (тип 2)
    строка(0x0008, 0x0060, 'CS', 'OT'),                       // Modality: Other — так у вторичного захвата
    строка(0x0008, 0x0064, 'CS', 'WSD'),                      // Conversion Type: рабочая станция
    строка(0x0008, 0x0090, 'PN', иссл.направил || ''),        // Referring Physician (тип 2)
    строка(0x0008, 0x1030, 'LO', иссл.описание || ''),        // Study Description
    строка(0x0008, 0x103E, 'LO', сер.описание || 'Снимок экрана'),
    строка(0x0010, 0x0010, 'PN', пац.имя || ''),              // Patient Name (тип 2)
    строка(0x0010, 0x0020, 'LO', пац.id || ''),               // Patient ID (тип 2)
    строка(0x0010, 0x0030, 'DA', пац.рождение || ''),         // Birth Date (тип 2)
    строка(0x0010, 0x0040, 'CS', пац.пол || ''),              // Sex (тип 2)
    строка(0x0020, 0x000D, 'UI', иссл.uid || новыйUID()),     // Study Instance UID — исходного исследования
    строка(0x0020, 0x000E, 'UI', сер.uid || новыйUID()),      // Series Instance UID — своя серия снимков
    строка(0x0020, 0x0010, 'SH', иссл.id || ''),              // Study ID (тип 2)
    строка(0x0020, 0x0011, 'IS', String(сер.номер ?? 999)),   // Series Number
    строка(0x0020, 0x0013, 'IS', String(экз.номер ?? 1)),     // Instance Number
    строка(0x0020, 0x0020, 'CS', ''),                         // Patient Orientation (тип 2C)
    us(0x0028, 0x0002, 3),                                    // Samples per Pixel
    строка(0x0028, 0x0004, 'CS', 'RGB'),
    us(0x0028, 0x0006, 0),                                    // Planar Configuration: R G B подряд
    us(0x0028, 0x0010, h), us(0x0028, 0x0011, w),
    us(0x0028, 0x0100, 8), us(0x0028, 0x0101, 8), us(0x0028, 0x0102, 7), us(0x0028, 0x0103, 0),
  ].filter(Boolean);
  // Pixel Data: ровно w·h·3 байта, чётная длина.
  const n = w * h * 3;
  const пиксели = new Uint8Array(n + (n % 2));
  пиксели.set(п.rgb.subarray(0, n));
  набор.push(элемент(0x7FE0, 0x0010, 'OB', пиксели));
  набор.sort((a, b) => (a.g - b.g) || (a.e - b.e));

  const мета = [
    элемент(0x0002, 0x0001, 'OB', new Uint8Array([0x00, 0x01])),   // File Meta Information Version
    строка(0x0002, 0x0002, 'UI', SOP_SC),
    строка(0x0002, 0x0003, 'UI', sop),
    строка(0x0002, 0x0010, 'UI', TS_EXPLICIT_LE),
    строка(0x0002, 0x0012, 'UI', `${ПРИСТАВКА_UID}.1`),
    строка(0x0002, 0x0013, 'SH', 'MIKHMEL4D'),
  ];
  const длинаМеты = мета.reduce((с, э) => с + э.out.length, 0);
  const части = [new Uint8Array(128), new Uint8Array([0x44, 0x49, 0x43, 0x4D]), ul(0x0002, 0x0000, длинаМеты).out, ...мета.map((э) => э.out), ...набор.map((э) => э.out)];
  const всего = части.reduce((с, ч) => с + ч.length, 0);
  const файл = new Uint8Array(всего);
  let о = 0;
  for (const ч of части) { файл.set(ч, о); о += ч.length; }
  return файл;
}

export default { собратьSC, ПРИСТАВКА_UID };
